/**
 * Receipt upload: init -> PUT straight to object storage -> complete.
 *
 * The bytes never touch the API. XHR rather than fetch because fetch still
 * has no upload progress event, and on a hotel connection a progress bar is
 * the difference between "working" and "frozen". Do not "modernise" this.
 */
import { api } from './api';
import { compressReceipt } from './image';

export interface UploadInit {
  receiptId: string;
  uploadUrl: string;
  thumbUploadUrl: string | null;
}

export interface UploadResult {
  receiptId: string;
  originalSize: number;
  finalSize: number;
  needsServerTranscode: boolean;
  previewUrl: string;
}

export async function uploadReceipt(
  reportId: string,
  file: File,
  source: 'camera' | 'library' | 'desktop',
  onProgress?: (fraction: number) => void,
): Promise<UploadResult> {
  const image = await compressReceipt(file);
  onProgress?.(0.1);

  const init = await api.post<UploadInit>(`/reports/${reportId}/receipts`, {
    filename: file.name || 'receipt.jpg',
    contentType: image.compressed ? 'image/jpeg' : image.originalType,
    sizeBytes: image.full.size,
    hasThumbnail: image.thumb !== null,
    originalContentType: image.originalType,
    originalSizeBytes: image.originalSize,
    compressedClientSide: image.compressed,
    ...(image.exifOrientation ? { exifOrientation: image.exifOrientation } : {}),
    captureSource: source,
    needsServerTranscode: image.needsServerTranscode,
    ...(image.width ? { width: image.width, height: image.height } : {}),
  });

  await putWithRetry(init.uploadUrl, image.full, image.compressed ? 'image/jpeg' : image.originalType,
    (f) => onProgress?.(0.1 + f * 0.8));

  if (init.thumbUploadUrl && image.thumb) {
    // A failed thumbnail must not fail the receipt -- the UI falls back to an
    // icon and the server-side repair job can fill it in later.
    try {
      await putWithRetry(init.thumbUploadUrl, image.thumb, 'image/jpeg');
    } catch {
      /* ignore */
    }
  }

  await api.post(`/receipts/${init.receiptId}/complete`);
  onProgress?.(1);

  return {
    receiptId: init.receiptId,
    originalSize: image.originalSize,
    finalSize: image.full.size,
    needsServerTranscode: image.needsServerTranscode,
    previewUrl: URL.createObjectURL(image.full),
  };
}

/** Retry is not optional on cellular; three attempts with backoff. */
async function putWithRetry(
  url: string,
  blob: Blob,
  contentType: string,
  onProgress?: (fraction: number) => void,
  attempts = 3,
): Promise<void> {
  let lastError: unknown;
  for (let attempt = 1; attempt <= attempts; attempt += 1) {
    try {
      await putToStorage(url, blob, contentType, onProgress);
      return;
    } catch (err) {
      lastError = err;
      if (attempt < attempts) {
        await new Promise((r) => setTimeout(r, 2 ** attempt * 500));
      }
    }
  }
  throw lastError instanceof Error ? lastError : new Error('Upload failed');
}

function putToStorage(
  url: string,
  blob: Blob,
  contentType: string,
  onProgress?: (fraction: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url, true);
    xhr.setRequestHeader('Content-Type', contentType);

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded / e.total);
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Storage rejected the upload (${xhr.status})`));
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.ontimeout = () => reject(new Error('Upload timed out'));
    xhr.timeout = 120_000;
    xhr.send(blob);
  });
}

export async function abortReceipt(receiptId: string): Promise<void> {
  try {
    await api.delete(`/receipts/${receiptId}`);
  } catch {
    // The nightly sweeper is the backstop; a failed abort is not worth
    // blocking the person over.
  }
}
